import React, { useState } from "react";
import { DollarSign, ChevronDown, ChevronRight } from "react-feather";
import { useHasPermission } from "../../../auth/useClaims";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { useDashboardPeriod } from "../../useDashboardPeriod";
import { useGetCollectionsSummaryQuery } from "../../../collections/collectionsApi";
import { useCollectionsRecords } from "../../useCollectionsRecords";
import { summarizePostDatedCheques } from "../../collectionsSummaryAggregator";
import { money, sumBy } from "../../dashboardHelpers";
import CollapsibleBranchStrip from "./CollapsibleBranchStrip";
import ChequesDueTodayCard from "./ChequesDueTodayCard";

// Ported from TexasAlarms_APP_FLUTTER's Dashboard Collections card/branch-
// strips - Collections only, per direct instruction (the other Flutter
// dashboard modules aren't in scope). Starts expanded (matches Flutter);
// reads period/branch from the page's existing global controls
// (DashboardPeriodControl/BranchPicker) rather than a second date picker.
const CollectionsDashboardSection = () => {
  const canView = useHasPermission("Collections.View");
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { period } = useDashboardPeriod();
  const [sectionExpanded, setSectionExpanded] = useState(true);

  const branchParams = { branchId: selectedBranchId ?? undefined, dateFrom: period.dateFrom, dateTo: period.dateTo };

  const { data: totalSummary, isFetching } = useGetCollectionsSummaryQuery(branchParams, { skip: !canView });
  const { data: byBranch = [] } = useGetCollectionsSummaryQuery(
    { ...branchParams, groupBy: "branch" },
    { skip: !canView }
  );
  const { rows: chequeRows } = useCollectionsRecords(
    { ...branchParams, paymentMethod: 4 },
    { skip: !canView }
  );

  if (!canView) return null;

  const total = sumBy(totalSummary, "totalAmount");
  const recordCount = sumBy(totalSummary, "totalCount");
  const postDated = summarizePostDatedCheques(chequeRows);
  const branchRows = [...byBranch].sort((a, b) => b.totalAmount - a.totalAmount);

  return (
    <div className="col-12">
      <div className="collections-section-card">
        <div className="collections-section-header" onClick={() => setSectionExpanded((v) => !v)}>
          <span className="collections-section-icon">
            <DollarSign size={16} />
          </span>
          <span className="collections-section-title flex-grow-1">Collections</span>
          {sectionExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </div>

        {sectionExpanded && (
          <div className="collections-section-body">
            <div className="collections-summary-box">
              <div className="text-muted small text-uppercase fw-semibold mb-1">Total Collections</div>
              <div className="fs-4 fw-bold">
                {isFetching ? "..." : money(total)} <span className="fs-6 text-muted fw-normal">· {recordCount} record{recordCount === 1 ? "" : "s"}</span>
              </div>
              <hr className="my-2" />
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">Post-Dated Cheques</span>
                <span className="fw-semibold small">{postDated.count} · {money(postDated.amount)}</span>
              </div>
              <ChequesDueTodayCard />
            </div>

            {branchRows.length === 0 ? (
              <div className="text-muted small mt-3">No collections in this period.</div>
            ) : (
              <div className="mt-3 d-flex flex-column gap-2">
                {branchRows.map((b) => {
                  const branchId = Number(b.groupKey);
                  const branchMeta = availableBranches.find((br) => br.branchId === branchId);
                  return (
                    <CollapsibleBranchStrip
                      key={b.groupKey}
                      branchId={branchId}
                      branchLabel={branchMeta?.branchCode || b.groupLabel}
                      totalAmount={b.totalAmount}
                      dateFrom={period.dateFrom}
                      dateTo={period.dateTo}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionsDashboardSection;
